/**
 * Opening the originals of mirrors (spec §8.18): for pasting, detaching and pinning.
 */
import type { MirrorSource } from "@kss/schema";
import type { TreeID } from "loro-crdt";
import { openDoc, type DocHandle } from "../../sync/docManager";
import { blocksTree } from "../model";
import { snapBlock, type BlockSnap } from "./snapshot";

const JOIN_WAIT_MS = 10_000;

/** Open a page and wait until its first join has settled (or time out), so its blocks are present. */
export async function openSettled(docId: string): Promise<DocHandle> {
  const h = await openDoc("page", docId);
  if (!h.joinSettled) {
    await new Promise<void>((resolve) => {
      const timer = setTimeout(done, JOIN_WAIT_MS);
      const unsub = h.subscribeStatus(() => h.joinSettled && done());
      function done() {
        clearTimeout(timer);
        unsub();
        resolve();
      }
    });
  }
  return h;
}

export function parseSource(source: MirrorSource): { docId: string; treeId?: TreeID } | null {
  const m = source.kind === "block" ? /^urn:km:blk:([0-9a-f-]{36}):(\d+@\d+)$/.exec(source.iri) : /^urn:km:doc:([0-9a-f-]{36})$/.exec(source.iri);
  if (!m) return null;
  return { docId: m[1]!, treeId: m[2] as TreeID | undefined };
}

/** The original's current blocks, from its page opened for the duration. */
export async function resolveSource(source: MirrorSource): Promise<BlockSnap[] | null> {
  const parsed = parseSource(source);
  if (!parsed) return null;
  const h = await openSettled(parsed.docId);
  try {
    const tree = blocksTree(h.doc);
    if (parsed.treeId) {
      const node = tree.getNodeByID(parsed.treeId);
      return node && !tree.isNodeDeleted(parsed.treeId) ? [snapBlock(parsed.docId, node)] : null;
    }
    return tree.roots().map((n) => snapBlock(parsed.docId, n));
  } finally {
    h.release();
  }
}

